import React from 'react';
import { Alert, Empty, Spin, Tag, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useTriSourceSignal } from '@/api/weatherTriSource';

const { Text } = Typography;

const decisionColor: Record<string, string> = {
  enhance: '#cf1322',
  maintain: '#ad6800',
  low_confidence: '#8c8c8c',
  discount_official: '#d48806',
  reduce: '#237804',
};

const confidenceColor: Record<string, string> = {
  high: '#237804',
  medium: '#ad6800',
  low: '#a8071a',
};

const levelTagColor: Record<string, string> = {
  high: 'volcano',
  medium: 'gold',
  low: 'green',
};

function fmt(val: number | null | undefined, digits = 1): string {
  if (val === null || val === undefined || Number.isNaN(val)) return '-';
  return val.toFixed(digits);
}

const WeatherTriSourcePanel: React.FC = () => {
  const { data, isLoading, error } = useTriSourceSignal();


  if (isLoading) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Spin tip="加载三源校验判断..." />
      </section>
    );
  }

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="三源校验判断暂不可用"
        description="无法加载三源信号，气象地图和其他模块仍可使用。"
        style={{ marginBottom: 14 }}
      />
    );
  }

  if (!data?.summary) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Empty description={data?.message ?? '暂无三源校验数据'} />
      </section>
    );
  }

  const { summary, sources, conflicts, trading_notes } = data;
  const { weather_index, official_forecast, weather_bulletin, recent_realization } = sources;


  return (
    <section
      style={{
        border: '1px solid #d9e2ec',
        borderRadius: 8,
        background: '#f8fafc',
        padding: 16,
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ThunderboltOutlined style={{ color: '#d48806' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>三源校验判断</div>
        <Tag color={levelTagColor[summary.solar_pressure_level]}>
          光伏压价压力: {summary.solar_pressure_level === 'high' ? '高' : summary.solar_pressure_level === 'low' ? '低' : '中'}
        </Tag>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            background: decisionColor[summary.decision],
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {summary.decision_label}
        </div>
        <Text style={{ color: confidenceColor[summary.confidence], fontWeight: 600 }}>
          可信度: {summary.confidence === 'high' ? '高' : summary.confidence === 'low' ? '低' : '中'}
        </Text>
      </div>

      <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>
        {summary.headline}
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 12 }}>
        {/* IFS metrics */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: '8px 12px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>IFS 光伏指数</Text>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#f97316' }}>{fmt(weather_index.solar_midday_index)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>10-14时 · 等级: {weather_index.solar_level === 'high' ? '高' : weather_index.solar_level === 'low' ? '低' : '中'}</Text>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: '8px 12px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>IFS 风电指数</Text>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#0f766e' }}>{fmt(weather_index.wind_daily_index)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>全天 · 等级: {weather_index.wind_level === 'high' ? '高' : weather_index.wind_level === 'low' ? '低' : '中'}</Text>
        </div>
        {/* Official metrics */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: '8px 12px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>官方光伏指数</Text>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#d48806' }}>{fmt(official_forecast.solar_midday_index)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>10-14时 · {fmt(official_forecast.solar_midday_mw)} MW</Text>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: '8px 12px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>官方风电指数</Text>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#0f766e' }}>{fmt(official_forecast.wind_daily_index)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>全天 · {fmt(official_forecast.wind_daily_mw)} MW</Text>
        </div>
      </div>


      {/* Bulletin risk and realization */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {weather_bulletin.available && weather_bulletin.has_risk && (
          <Tag color="orange">
            简报: {weather_bulletin.key_regions_affected.join('、') || '有云雨风险'}
          </Tag>
        )}
        {weather_bulletin.available && !weather_bulletin.has_risk && (
          <Tag color="green">简报: 无明显削弱</Tag>
        )}
        {!weather_bulletin.available && (
          <Tag>简报: 未可用</Tag>
        )}
        {recent_realization.curtailment_watch && (
          <Tag color="red">
            兑现风险: 近7天有{recent_realization.watch_days}天兑现率&lt;80%
          </Tag>
        )}
        {!recent_realization.curtailment_watch && recent_realization.days_analyzed > 0 && (
          <Tag color="green">近期兑现: 正常</Tag>
        )}
      </div>

      {/* Conflicts */}
      {conflicts.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 13 }}>冲突信号:</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {conflicts.map((c) => (
              <Text key={c} type="secondary" style={{ fontSize: 12 }}>{c}</Text>
            ))}
          </div>
        </div>
      )}

      {/* Trading notes */}
      {trading_notes.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 13 }}>交易注释:</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {trading_notes.map((n) => (
              <Text key={n} style={{ fontSize: 13, color: '#374151' }}>{n}</Text>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default WeatherTriSourcePanel;
